import { Check, ChevronDown } from 'lucide-react';
import type { ReactNode } from 'react';
import menuStyles from '../FilterMenu/FilterMenu.module.css';
import { useMenuPopover } from '../FilterMenu/useMenuPopover.ts';
import styles from './MultiSelectMenu.module.css';

export type MultiSelectOption = { readonly value: string; readonly label: string; readonly detail?: string };

export type MultiSelectPreset = { readonly label: string; readonly values: readonly string[] };

export type MultiSelectMenuProps = {
  label: string;
  summary: string;
  icon?: ReactNode;
  options: readonly MultiSelectOption[];
  value: readonly string[];
  presets?: readonly MultiSelectPreset[];
  onChange: (values: readonly string[]) => void;
};

const toggled = (values: readonly string[], value: string): readonly string[] =>
  values.includes(value) ? values.filter((candidate) => candidate !== value) : [...values, value];

const sameSelection = (left: readonly string[], right: readonly string[]): boolean =>
  left.length === right.length && left.every((value) => right.includes(value));

export const MultiSelectMenu = ({
  label,
  summary,
  icon,
  options,
  value,
  presets = [],
  onChange,
}: MultiSelectMenuProps) => {
  const { open, toggle, menuId, root, list, trigger, moveWithKeys, place } = useMenuPopover(0);
  const ordered = (values: readonly string[]) =>
    options.filter((option) => values.includes(option.value)).map((option) => option.value);

  return (
    <div ref={root} className={menuStyles['root']}>
      <button
        ref={trigger}
        type="button"
        className={`${menuStyles['trigger'] ?? ''} ${styles['trigger'] ?? ''}`}
        data-active="true"
        aria-label={`${label}, ${summary}`}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={toggle}
      >
        {icon}
        <span>{label}</span>
        <strong className={menuStyles['chosen']}>{summary}</strong>
        <ChevronDown size={16} aria-hidden="true" className={styles['chevron']} />
      </button>
      {open ? (
        <div
          ref={list}
          id={menuId}
          role="menu"
          tabIndex={-1}
          aria-label={label}
          className={`${menuStyles['menu'] ?? ''} ${styles['menu'] ?? ''}`}
          style={place}
          onKeyDown={moveWithKeys}
        >
          {presets.length === 0 ? null : (
            <div className={styles['presets']}>
              {presets.map((preset) => (
                <button
                  key={preset.label}
                  type="button"
                  role="menuitem"
                  className={styles['preset']}
                  data-current={sameSelection(preset.values, value)}
                  disabled={preset.values.length === 0}
                  onClick={() => {
                    onChange(ordered(preset.values));
                  }}
                >
                  {preset.label}
                </button>
              ))}
            </div>
          )}
          {options.map((option) => {
            const checked = value.includes(option.value);
            const lastOne = checked && value.length === 1;
            return (
              <button
                key={option.value}
                type="button"
                role="menuitemcheckbox"
                aria-checked={checked}
                aria-disabled={lastOne}
                className={`${menuStyles['item'] ?? ''} ${styles['item'] ?? ''}`}
                onClick={() => {
                  if (!lastOne) onChange(ordered(toggled(value, option.value)));
                }}
              >
                <span className={styles['box']} data-checked={checked} aria-hidden="true">
                  {checked ? <Check size={12} strokeWidth={3} /> : null}
                </span>
                <span className={styles['label']}>{option.label}</span>
                {option.detail === undefined ? null : (
                  <span className={styles['detail']}>{option.detail}</span>
                )}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
};
